import type { StateCreator } from 'zustand';
import { UploadSlice } from './types';
import type { DocumentationStoreSlices } from '../types';
import {
  createDocumentationUploadService,
} from 'services/documentationUploadService';
import { getProjectInfo } from 'store/project';
import { getCurrentUser } from 'store/user';
import {
  UploadConfig,
} from 'services/documentationUpload';

export * from './types';

const INITIAL_UPLOAD_STATE = {
  isUploading: false,
  uploadProgress: 0,
  uploadedCount: 0,
  totalCount: 0,
  uploadedDirectoryHighlightIds: [] as string[],
  uploadedDocumentationHighlightLocalIds: [] as number[],
};

type UploadSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  UploadSlice
>;

const documentationUploadService = createDocumentationUploadService();

function resolveUploadConfig(): UploadConfig {
  const currentUser = getCurrentUser();
  const { _id: projectId, organizationId } = getProjectInfo();

  return {
    currentUser,
    organizationId,
    projectId,
  };
}

export const createUploadSlice: UploadSliceCreator = (set, get) => ({
  ...INITIAL_UPLOAD_STATE,

  addDocumentationsFromFiles: async (
    files: File[],
    targetDirectoryId?: string | null
  ): Promise<void> => {
    const { currentDirectory, fetchDirectoryContent } = get();
    const parentDirectoryId = targetDirectoryId ?? currentDirectory;

    set({ 
      isUploading: true, 
      totalCount: files.length,
      uploadedCount: 0,
    });

    try {
      const { createdDocumentationLocalIds } =
        await documentationUploadService.uploadDocumentations(
          files,
          parentDirectoryId,
          resolveUploadConfig()
        );

      get().markUploadedDocumentationsForHighlight(createdDocumentationLocalIds);

      await fetchDirectoryContent(parentDirectoryId);
    } finally {
      set({ 
        isUploading: false,
        uploadProgress: 0,
        uploadedCount: 0,
        totalCount: 0,
      });
    }
  },

  addDirectoriesFromFiles: async (
    files: File[],
    targetDirectoryId?: string | null,
    emptyDirectoryPaths: string[] = []
  ): Promise<void> => {
    const { currentDirectory, fetchDirectoryContent, fetchAllDirectories } = get();
    const parentDirectoryId = targetDirectoryId ?? currentDirectory;

    set({
      isUploading: true,
      totalCount: files.length,
      uploadedCount: 0,
    });

    try {
      const { createdDirectoryIds, createdDocumentationLocalIds } =
        await documentationUploadService.uploadDirectoriesWithFiles(
          files,
          parentDirectoryId,
          resolveUploadConfig(),
          emptyDirectoryPaths
        );

      get().markUploadedDirectoriesForHighlight(createdDirectoryIds);
      get().markUploadedDocumentationsForHighlight(createdDocumentationLocalIds);

      await fetchAllDirectories();
      await fetchDirectoryContent(parentDirectoryId);
    } finally {
      set({ 
        isUploading: false,
        uploadProgress: 0,
        uploadedCount: 0,
        totalCount: 0,
      });
    }
  },

  assignFilesToDirectories: async (
    files: File[],
    directoriesMap: Record<string, string>
  ): Promise<void> => {
    set({ 
      isUploading: true, 
      totalCount: files.length,
      uploadedCount: 0,
    });

    try {
      await documentationUploadService.assignFilesToDirectories(
        files,
        directoriesMap,
        resolveUploadConfig()
      );
    } finally {
      set({ 
        isUploading: false,
        uploadProgress: 0,
        uploadedCount: 0,
        totalCount: 0,
      });
    }
  },

  setUploadProgress: (uploaded: number, total: number): void => {
    set({ 
      uploadedCount: uploaded, 
      totalCount: total,
      uploadProgress: total > 0 ? (uploaded / total) * 100 : 0,
    });
  },

  markUploadedDirectoriesForHighlight: (directoryIds: string[]): void => {
    if (!directoryIds.length) return;

    const existing = get().uploadedDirectoryHighlightIds;
    set({
      uploadedDirectoryHighlightIds: Array.from(
        new Set([...existing, ...directoryIds])
      ),
    });
  },

  markUploadedDocumentationsForHighlight: (localIds: number[]): void => {
    if (!localIds.length) return;

    const existing = get().uploadedDocumentationHighlightLocalIds;
    set({
      uploadedDocumentationHighlightLocalIds: Array.from(
        new Set([...existing, ...localIds])
      ),
    });
  },

  clearUploadedDirectoryHighlight: (directoryId: string): void => {
    if (!directoryId) return;

    set({
      uploadedDirectoryHighlightIds: get().uploadedDirectoryHighlightIds.filter(
        (id) => id !== directoryId
      ),
    });
  },

  clearUploadedDocumentationHighlight: (localId: number): void => {
    set({
      uploadedDocumentationHighlightLocalIds:
        get().uploadedDocumentationHighlightLocalIds.filter((id) => id !== localId),
    });
  },

  clearUploadedHighlights: (): void => {
    set({
      uploadedDirectoryHighlightIds: [],
      uploadedDocumentationHighlightLocalIds: [],
    });
  },

  resetUploadState: (): void => {
    set(INITIAL_UPLOAD_STATE);
  },
});
